<template>
  <div>
    <div class="filter-bar">
      <el-select
        v-model="filters.status"
        placeholder="All Statuses"
        clearable
        @change="loadGateways"
        size="small"
        style="width:160px"
      >
        <el-option v-for="s in statuses" :key="s" :label="s" :value="s" />
      </el-select>
      <el-select
        v-model="filters.frequencyPlan"
        placeholder="All Freq Plans"
        clearable
        @change="loadGateways"
        size="small"
        style="width:180px"
      >
        <el-option v-for="f in frequencyPlans" :key="f" :label="f" :value="f" />
      </el-select>
      <el-button size="small" icon="el-icon-refresh" @click="loadGateways"
        >Refresh</el-button
      >
      <span class="result-count">{{ gateways.length }} gateways</span>
      <el-button
        v-if="$permission('Installation Tool.Devices.Add')"
        type="primary"
        size="small"
        icon="el-icon-plus"
        @click="openCreateModal"
        >Add Gateway</el-button
      >
    </div>

    <el-table
      :data="gateways"
      v-loading="loading"
      border
      class="app-table"
      :max-height="tableHeight"
    >
      <el-table-column prop="gatewayEui" label="Gateway EUI" width="200" />
      <el-table-column prop="model" label="Model" width="160" />
      <el-table-column prop="supplier" label="Supplier" width="160" />
      <el-table-column prop="frequencyPlan" label="Freq Plan" width="140" />
      <el-table-column
        prop="cupsEnabled"
        label="CUPS"
        width="90"
        align="center"
      >
        <template slot-scope="{ row }">
          <i
            :class="
              row.cupsEnabled
                ? 'el-icon-check text-success'
                : 'el-icon-close text-muted'
            "
          />
        </template>
      </el-table-column>
      <el-table-column prop="class" label="Class" width="80" align="center" />
      <el-table-column prop="status" label="Status" width="130">
        <template slot-scope="{ row }">
          <el-tag :type="statusType(row.status)" size="small">{{
            row.status
          }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column
        prop="friendlyName"
        label="Friendly Name"
        min-width="160"
        show-overflow-tooltip
      />
      <el-table-column label="Actions" width="230" align="center" fixed="right">
        <template slot-scope="{ row }">
          <el-button
            v-if="
              (row.status === 'Commissioned' || row.status === 'Assigned') &&
                $permission('Installation Tool.Devices.Edit')
            "
            size="small"
            type="primary"
            plain
            @click="openReturnModal(row)"
            >Return</el-button
          >
          <el-button
            v-if="
              row.status !== 'Decommissioned' &&
                $permission('Installation Tool.Devices.Edit')
            "
            size="small"
            type="danger"
            plain
            @click="openDecommissionModal(row)"
            >Decommission</el-button
          >
        </template>
      </el-table-column>
    </el-table>

    <!-- Add Gateway Modal -->
    <el-dialog
      title="Add Gateway"
      :visible.sync="showCreateModal"
      width="520px"
      @close="resetCreateForm"
    >
      <el-form
        :model="createForm"
        :rules="createRules"
        ref="createForm"
        label-width="130px"
      >
        <el-form-item label="Gateway EUI" prop="gatewayEui">
          <el-input
            v-model="createForm.gatewayEui"
            placeholder="16 hex characters"
          />
        </el-form-item>
        <el-form-item label="Model" prop="model">
          <el-input v-model="createForm.model" placeholder="e.g. LG308" />
        </el-form-item>
        <el-form-item label="Supplier" prop="supplier">
          <el-input v-model="createForm.supplier" placeholder="e.g. Lansitec" />
        </el-form-item>
        <el-form-item label="Frequency Plan" prop="frequencyPlan">
          <el-select
            v-model="createForm.frequencyPlan"
            placeholder="Select"
            style="width:100%"
          >
            <el-option
              v-for="f in frequencyPlans"
              :key="f"
              :label="f"
              :value="f"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="LoRaWAN Class">
          <el-select
            v-model="createForm.class"
            placeholder="Optional"
            style="width:100%"
            clearable
          >
            <el-option label="Class A" value="A" />
            <el-option label="Class B" value="B" />
            <el-option label="Class C" value="C" />
          </el-select>
        </el-form-item>
        <el-form-item label="CUPS Enabled">
          <el-switch
            v-model="createForm.cupsEnabled"
            active-text="Yes"
            inactive-text="No"
          />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button class="save-btn" :loading="saving" @click="createGateway"
          >ADD GATEWAY</el-button
        >
        <el-button class="cancel-btn" @click="showCreateModal = false"
          >CANCEL</el-button
        >
      </span>
    </el-dialog>

    <!-- Return to Pool Modal -->
    <el-dialog
      title="Return to Pool"
      :visible.sync="showReturnModal"
      width="420px"
    >
      <p>
        Gateway
        <strong>{{ actionTarget && actionTarget.gatewayEui }}</strong> will be
        removed from LNS and returned to the unassigned pool.
      </p>
      <el-form :model="actionForm" ref="returnForm" label-width="80px">
        <el-form-item
          label="Reason"
          :rules="[{ required: true, message: 'Required', trigger: 'blur' }]"
          prop="reason"
        >
          <el-input
            type="textarea"
            :rows="3"
            v-model="actionForm.reason"
            placeholder="Reason for return"
          />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button class="save-btn" :loading="acting" @click="returnToPool"
          >CONFIRM RETURN</el-button
        >
        <el-button class="cancel-btn" @click="showReturnModal = false"
          >CANCEL</el-button
        >
      </span>
    </el-dialog>

    <!-- Decommission Modal -->
    <el-dialog
      title="Decommission Gateway"
      :visible.sync="showDecommissionModal"
      width="420px"
    >
      <el-alert
        type="error"
        title="This action is permanent and cannot be undone."
        show-icon
        :closable="false"
        style="margin-bottom:16px"
      />
      <p>
        Gateway
        <strong>{{ actionTarget && actionTarget.gatewayEui }}</strong> will be
        permanently decommissioned.
      </p>
      <el-form :model="actionForm" ref="decommissionForm" label-width="80px">
        <el-form-item
          label="Reason"
          :rules="[{ required: true, message: 'Required', trigger: 'blur' }]"
          prop="reason"
        >
          <el-input
            type="textarea"
            :rows="3"
            v-model="actionForm.reason"
            placeholder="Reason for decommission"
          />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button class="delete-btn" :loading="acting" @click="decommission"
          >CONFIRM DECOMMISSION</el-button
        >
        <el-button class="cancel-btn" @click="showDecommissionModal = false"
          >CANCEL</el-button
        >
      </span>
    </el-dialog>
  </div>
</template>

<script>
import LoraApiService from '@/services/LoraApiService'
import { loraError } from '@/utils/loraError'
import loraTableHeight from '@/mixins/loraTableHeight'

const FREQUENCY_PLANS = ['AU915', 'EU868', 'US915', 'AS923', 'CN470']
const STATUSES = ['Unassigned', 'Assigned', 'Commissioned', 'Decommissioned']

const emptyCreate = () => ({
  gatewayEui: '',
  model: '',
  supplier: '',
  frequencyPlan: '',
  class: '',
  cupsEnabled: false
})

export default {
  name: 'LoraGatewayPool',
  mixins: [loraTableHeight],
  data() {
    return {
      gateways: [],
      loading: false,
      saving: false,
      acting: false,
      filters: { status: '', frequencyPlan: '' },
      frequencyPlans: FREQUENCY_PLANS,
      statuses: STATUSES,
      showCreateModal: false,
      createForm: emptyCreate(),
      createRules: {
        gatewayEui: [{ required: true, message: 'Required', trigger: 'blur' }],
        model: [{ required: true, message: 'Required', trigger: 'blur' }],
        supplier: [{ required: true, message: 'Required', trigger: 'blur' }],
        frequencyPlan: [
          { required: true, message: 'Required', trigger: 'change' }
        ],
        cupsEnabled: [{ required: true }]
      },
      showReturnModal: false,
      showDecommissionModal: false,
      actionTarget: null,
      actionForm: { reason: '' }
    }
  },
  mounted() {
    this.loadGateways()
  },
  methods: {
    async loadGateways() {
      this.loading = true
      try {
        const params = {}
        if (this.filters.status) params.status = this.filters.status
        if (this.filters.frequencyPlan)
          params.frequencyPlan = this.filters.frequencyPlan
        const res = await LoraApiService.getGateways(params)
        this.gateways = res.data || []
      } catch {
        this.$message.error('Failed to load gateways')
      } finally {
        this.loading = false
      }
    },
    openCreateModal() {
      this.createForm = emptyCreate()
      this.showCreateModal = true
    },
    resetCreateForm() {
      this.$refs.createForm && this.$refs.createForm.resetFields()
    },
    async createGateway() {
      const valid = await this.$refs.createForm.validate().catch(() => false)
      if (!valid) return
      this.saving = true
      try {
        await LoraApiService.createGateway(this.createForm)
        this.$message.success('Gateway added to pool')
        this.showCreateModal = false
        this.loadGateways()
      } catch (e) {
        this.$message.error(loraError(e, 'Failed to add gateway'))
      } finally {
        this.saving = false
      }
    },
    openReturnModal(gw) {
      this.actionTarget = gw
      this.actionForm = { reason: '' }
      this.showReturnModal = true
    },
    async returnToPool() {
      if (!this.actionForm.reason) {
        this.$message.warning('Please provide a reason')
        return
      }
      this.acting = true
      try {
        await LoraApiService.returnGatewayToPool(
          this.actionTarget.id,
          this.actionForm.reason
        )
        this.$message.success('Gateway returned to pool')
        this.showReturnModal = false
        this.loadGateways()
      } catch (e) {
        this.$message.error(loraError(e, 'Failed'))
      } finally {
        this.acting = false
      }
    },
    openDecommissionModal(gw) {
      this.actionTarget = gw
      this.actionForm = { reason: '' }
      this.showDecommissionModal = true
    },
    async decommission() {
      if (!this.actionForm.reason) {
        this.$message.warning('Please provide a reason')
        return
      }
      this.acting = true
      try {
        await LoraApiService.decommissionGateway(
          this.actionTarget.id,
          this.actionForm.reason
        )
        this.$message.success('Gateway decommissioned')
        this.showDecommissionModal = false
        this.loadGateways()
      } catch (e) {
        this.$message.error(loraError(e, 'Failed'))
      } finally {
        this.acting = false
      }
    },
    statusType(s) {
      return (
        {
          Unassigned: 'info',
          Assigned: 'warning',
          Commissioned: 'success',
          Decommissioned: 'danger'
        }[s] || 'info'
      )
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/varible.scss';

.filter-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  .result-count {
    margin-left: auto;
    font-size: 13px;
    color: $font-color;
  }
}

.text-success {
  color: #67c23a;
}
.text-muted {
  color: #c0c4cc;
}
.text-danger {
  color: #f56c6c;
}
</style>
